import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import {
  getOisterBonusPointOverview,
  OisterBonusPointOverview,
} from "../../api/business";
import { formatInteger, formatNumber } from "../../helpers/formatNumber";
import { dateToCopenhagenEndOfDayUTC } from "../../helpers/dateFilters";

// Platform-wide point balance — snapshot as of the picked date.
// Defaults to today (= current). Re-fetches whenever the date changes.
const PlatformPointBalanceCard: React.FC = () => {
  const [pointOverview, setPointOverview] =
    useState<OisterBonusPointOverview | null>(null);
  const [pointOverviewLoading, setPointOverviewLoading] = useState(true);
  const [pointOverviewAsOf, setPointOverviewAsOf] = useState<string>(() =>
    format(new Date(), "yyyy-MM-dd")
  );

  useEffect(() => {
    setPointOverviewLoading(true);
    const asOfIso = pointOverviewAsOf
      ? dateToCopenhagenEndOfDayUTC(pointOverviewAsOf)
      : undefined;
    getOisterBonusPointOverview(asOfIso)
      .then(setPointOverview)
      .catch(() => setPointOverview(null))
      .finally(() => setPointOverviewLoading(false));
  }, [pointOverviewAsOf]);

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h3 className="text-lg font-medium">Platform Point Balance</h3>
        <div className="flex items-center gap-2">
          <label htmlFor="ppb-as-of" className="text-sm text-gray-600">
            As of
          </label>
          <input
            id="ppb-as-of"
            type="date"
            className="p-1.5 border border-gray-300 rounded-md text-sm"
            value={pointOverviewAsOf}
            onChange={(e) => setPointOverviewAsOf(e.target.value)}
          />
        </div>
      </div>
      {pointOverviewLoading ? (
        <div className="text-gray-400 text-sm">Loading…</div>
      ) : pointOverview ? (
        <div className="flex flex-wrap gap-8">
          <div className="min-w-[150px]">
            <div className="text-gray-500 text-sm">
              Point balance on platform
            </div>
            <div className="text-2xl font-semibold mt-1">
              {formatNumber(pointOverview.totalPointBalance, 2)} p
            </div>
          </div>
          <div className="min-w-[150px]">
            <div className="text-gray-500 text-sm">
              Users with balance &gt; 0
            </div>
            <div className="text-2xl font-semibold mt-1">
              {formatInteger(pointOverview.usersWithPositiveBalance)}
            </div>
          </div>
          <div className="min-w-[150px]">
            <div className="text-gray-500 text-sm">
              Users with balance &gt; 500 points
            </div>
            <div className="text-2xl font-semibold mt-1">
              {formatInteger(pointOverview.usersWithBalanceAbove500)}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-gray-400 text-sm">Data unavailable</div>
      )}
    </div>
  );
};

export default PlatformPointBalanceCard;
